import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ResponsiveService } from '@service/responsive.service';
import { CustomGroup } from 'ops/common/models/custom-group';
import { TrendGraphView } from 'ops/common/models/trend-graph-view';

const SCROLL_OPTION_SMOOTH = 'smooth';
const SCROLL_OPTION_AUTO = 'auto';

type Item = CustomGroup | TrendGraphView;

type ScrollOption = typeof SCROLL_OPTION_SMOOTH | typeof SCROLL_OPTION_AUTO;
type DefaultItem = {
    displayName: string;
    routerLink: string;
};

@Component({
    selector: 'app-scroll-navigator',
    templateUrl: './scroll-navigator.component.html',
    styleUrls: ['./scroll-navigator.component.scss'],
    standalone: false,
})
export class ScrollNavigatorComponent {
    readonly ITEM_WIDTH = 160;
    readonly ITEM_MARGIN = 48;
    readonly ITEM_SCROLL_MOVE = this.ITEM_WIDTH + this.ITEM_MARGIN;

    @Input() items!: Item[];
    @Input() selectedId!: string | number;
    @Input() routerPath!: string[];
    @Input() defaultItem!: DefaultItem;
    // Generates the URL based on indexMode uses array index (true) or uses object id (false).
    @Input() indexMode: boolean = false;
    @Input() showDefaultItem: boolean = false;

    @ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef;

    shouldShowArrowButtons: boolean = false;

    public routerLinks: string[][] = [];
    public oldItemsNum: number = -1;

    constructor(
        public responsive: ResponsiveService,
        public translate: TranslateService,
        public routes: ActivatedRoute,
        private cdr: ChangeDetectorRef,
    ) {}

    ngAfterViewChecked(): void {
        if (this.oldItemsNum !== this.items.length) {
            this.oldItemsNum = this.items.length;
            const routerPathIdPosition = this.checkroutePathPosition();
            this.routerLinks = this.createIdRouterLinks(routerPathIdPosition);

            if (this.checkIndexMode()) {
                this.scrollMoveIndexRouter();
            } else {
                const findIndex = this.items.findIndex((item) => item.id === this.selectedId);
                findIndex !== -1
                    ? this.scrollMoveIdRouter(findIndex)
                    : this.scrollToStartingPoint();
            }
        }
        this.checkArrowButtonThreshold();
        this.cdr.detectChanges();
    }

    checkDefaultItem(): boolean {
        return !!this.defaultItem;
    }
    checkIndexMode(): boolean {
        return !!this.indexMode;
    }
    checkSelectedId(): boolean {
        return !!this.selectedId;
    }
    checkScrollContainer(): boolean {
        return !!this.scrollContainer;
    }
    checkArrowButtonThreshold() {
        if (this.checkScrollContainer()) {
            const element = this.scrollContainer.nativeElement;
            this.shouldShowArrowButtons = element.scrollWidth > element.clientWidth;
        }
    }
    checkroutePathPosition(): number {
        return this.routerPath.findIndex((route) => route === '');
    }

    scrollRight(): void {
        return this.scrollMove(this.ITEM_SCROLL_MOVE, SCROLL_OPTION_SMOOTH);
    }

    scrollLeft(): void {
        return this.scrollMove(-this.ITEM_SCROLL_MOVE, SCROLL_OPTION_SMOOTH);
    }

    scrollMove(moveDistance: number, smoothOps: ScrollOption): void {
        if (this.checkScrollContainer()) {
            this.scrollContainer.nativeElement.scrollBy({
                left: moveDistance,
                behavior: smoothOps,
            });
        }
    }
    scrollToStartingPoint() {
        if (this.checkScrollContainer()) {
            this.scrollContainer.nativeElement.scrollTo({
                left: 0,
            });
        }
    }

    scrollMoveIndexRouter() {
        this.scrollMove(this.ITEM_SCROLL_MOVE * Number(this.selectedId), SCROLL_OPTION_AUTO);
    }
    scrollMoveIdRouter(findIndex: number) {
        const moveNum = this.checkDefaultItem() ? findIndex + 1 : findIndex;
        this.scrollMove(this.ITEM_SCROLL_MOVE * moveNum, SCROLL_OPTION_AUTO);
    }

    createIdRouterLinks(routerPathIdPosition: number): string[][] {
        return this.items.map((item, index) => {
            const routerPath = Object.assign([], this.routerPath);
            routerPath[routerPathIdPosition] = this.checkIndexMode() ? index : item.id;
            return routerPath;
        });
    }

    isActiveItem(index?, item?) {
        if (this.defaultItem && !this.selectedId && !item) {
            return true;
        } else if (this.indexMode && index >= 0) {
            return this.selectedId == index;
        } else if (this.selectedId && item) {
            return this.selectedId == item.id;
        }
    }
}
